import { Injectable, inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, take, timeout } from 'rxjs/operators';
import * as AppSelectors from '../../store/app/app.selectors';
import { LoaderService } from './loader/loader.service';

// How long the app must stay settled before a deferred action runs.
const SETTLE_MS = 400;
// A failed load never marks the data loaded: past this the action runs anyway.
const SETTLE_TIMEOUT_MS = 20000;

// Idle before the first login of a page load and after a logout; loading from
// the start of the main data chain until it has loaded.
type MainDataState = 'idle' | 'loading' | 'loaded';

@Injectable({
  providedIn: 'root'
})
export class AppStateService {
  private isInitializing$: Observable<boolean>;
  private readonly splashDone = new BehaviorSubject<boolean>(false);
  private readonly mainData = new BehaviorSubject<MainDataState>('idle');
  private readonly loader = inject(LoaderService);
  private readonly loaderIdle$ = toObservable(this.loader.isLoading).pipe(map(loading => !loading));

  /** True once the startup splash has faded out and the app is on screen. */
  public readonly splashDone$: Observable<boolean> = this.splashDone.asObservable();

  /** False while the main data chain loads: at startup and on every domain switch. */
  public readonly mainDataLoaded$: Observable<boolean> = this.mainData.pipe(
    map(state => state === 'loaded'),
    distinctUntilChanged()
  );

  constructor(private store: Store) {
    this.isInitializing$ = this.store.select(AppSelectors.getIsInitializing).pipe(shareReplay(1));
  }

  get isInitializing(): Observable<boolean> {
    return this.isInitializing$;
  }

  markSplashDone(): void {
    this.splashDone.next(true);
  }

  /** True once the main data chain has been started in this login, loaded or not. */
  get hasMainDataStarted(): boolean {
    return this.mainData.value !== 'idle';
  }

  markMainDataLoading(): void {
    this.mainData.next('loading');
  }

  markMainDataLoaded(): void {
    this.mainData.next('loaded');
  }

  /** After a logout: the shell of the next login starts its chain afresh. */
  resetMainData(): void {
    this.mainData.next('idle');
  }

  /** Emits once the app is on screen with its data loaded and nothing loading. */
  whenSettled(): Observable<void> {
    if (this.splashDone.value && this.mainData.value === 'loaded' && !this.loader.isLoading()) {
      return of(undefined);
    }

    return combineLatest([this.splashDone, this.mainDataLoaded$, this.loaderIdle$]).pipe(
      debounceTime(SETTLE_MS),
      filter(states => states.every(Boolean)),
      take(1),
      map(() => undefined),
      timeout({ first: SETTLE_TIMEOUT_MS, with: () => of(undefined) }),
    );
  }

  checkShouldShowLoader(callback: (isInitializing: boolean) => boolean): Observable<boolean> {
    return this.isInitializing$.pipe(
      map(isInitializing => callback(isInitializing))
    );
  }
}
